import { useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { usePlaySound, useLocalizedImage } from "common/utils";
import { Course } from "features/courses/types";
import {
  selectAllProgressesProgram,
  selectAllProgressesAlgorithm,
} from "features/courses/coursesSlice";
import {
  IconMedal,
  IconCourseProgressBlock,
  IconCourseProgressEndBlock,
  IconCourseProgressStartBlock,
} from "features/user/assets";

type CourseSummary = { finished: number; total: number };
type CourseStageProgress = {
  id: number;
  stage: { stage_id: number; finished: number; total: number };
};

type CourseDef = {
  course: Course;
  labelKey: string;
  art: string;
  selectProgresses: (state: RootState) => CourseStageProgress[];
  selectSummary: (state: RootState) => CourseSummary;
  isEnabled: (enableLab: boolean) => boolean;
};

const COURSE_DEFS: CourseDef[] = [
  {
    course: Course.ALGORITHM,
    labelKey: "MSG_COURSE_ALGORITHM",
    art: "course_soft_algorithm",
    selectProgresses: (state) =>
      selectAllProgressesAlgorithm(state) as unknown as CourseStageProgress[],
    selectSummary: (state) => state.courses.progresses.algorithm.summary,
    isEnabled: () => true,
  },
  {
    course: Course.ALGORITHM_TANGIBLE,
    labelKey: "MSG_COURSE_ALGORITHM_ADVANCED",
    art: "course_soft_algorithm_tangible",
    selectProgresses: (state) =>
      selectAllProgressesAlgorithm(state) as unknown as CourseStageProgress[],
    selectSummary: (state) => state.courses.progresses.algorithm.summary,
    isEnabled: (enableLab) => enableLab,
  },
  {
    course: Course.PROGRAM,
    labelKey: "MSG_COURSE_PROGRAM",
    art: "course_soft_program",
    selectProgresses: (state) =>
      selectAllProgressesProgram(state) as unknown as CourseStageProgress[],
    selectSummary: (state) => state.courses.progresses.program.summary,
    isEnabled: () => true,
  },
];

const STAGE_TOTAL = 7;

const computePercent = (summary: CourseSummary): number =>
  summary.total > 0 ? Math.floor((summary.finished / summary.total) * 100) : 0;

const ShelfTile = ({
  artUrl,
  percent,
  selected,
  onClick,
}: {
  artUrl: string;
  percent: number;
  selected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-col-el flex-center shrink-0 w-[140px] gap-2 rounded-2xl p-2 transition-all cursor-pointer ${
      selected
        ? "bg-white border-[3px] border-solid border-green-60 shadow-[0_0_0_4px_rgba(128,234,193,0.25)]"
        : "bg-transparent border-[3px] border-solid border-transparent hover:bg-white/60"
    }`}
  >
    <div className="flex-col-el flex-center w-[110px] h-[140px]">
      <img alt="course" src={artUrl} className="w-full h-full object-contain" />
    </div>
    <p className="text-body text-textcolor-black text-[18px] leading-none">{percent}%</p>
  </button>
);

const Hero = ({
  title,
  percent,
  finished,
  total,
}: {
  title: string;
  percent: number;
  finished: number;
  total: number;
}) => {
  const size = 184;
  const stroke = 20;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div
      className="flex-col-el shrink-0 w-[280px] self-stretch bg-white rounded-2xl p-4 items-center justify-between"
      style={{
        filter:
          "drop-shadow(-1px 1px 1px rgba(63, 74, 97, 0.15)) drop-shadow(1px 1px 3px rgba(63, 74, 97, 0.25))",
      }}
    >
      <p className="text-body text-textcolor-black text-[20px] text-center leading-tight">
        {title}
      </p>

      <div className="flex-col-el flex-center relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="rotate-[-90deg]">
          <defs>
            <linearGradient id="hero-ring-fill" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F9E532" />
              <stop offset="100%" stopColor="#FAAF85" />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#ede3cf"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#hero-ring-fill)"
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="flex-col-el flex-center absolute inset-0">
          <p className="text-body text-brown-60 text-[14px] leading-none">
            {I18n.t("MSG_SETTING_COURSE_ANSWER_STAGE")}
          </p>
          <div className="flex-row-el items-baseline gap-1 mt-2">
            <p className="text-body text-textcolor-black text-[48px] leading-none">{finished}</p>
            <p className="text-body text-brown-60 text-[22px] leading-none">/{total}</p>
          </div>
        </div>
      </div>

      <div className="flex-row-el items-center px-4 py-1.5 rounded-full bg-green-60/25">
        <p className="text-body text-green-80 text-[18px] leading-none">
          {percent}% {I18n.t("MSG_COURSE_GAME_CLEAR")}
        </p>
      </div>
    </div>
  );
};

const StageCard = ({ index, finished }: { index: number; finished: number; total: number }) => (
  <div
    className="flex-row-el items-end relative w-full"
    style={{
      filter:
        "drop-shadow(-1px 1px 1px rgba(63, 74, 97, 0.15)) drop-shadow(1px 1px 3px rgba(63, 74, 97, 0.25))",
    }}
  >
    <div className="flex-col-el flex-center shrink-0 w-[44px] h-[44px] border-solid border-white border-[6px] rounded-full bg-danger -mr-7 z-10">
      <p className="text-body text-white text-[20px]">{index}</p>
    </div>
    <div className="flex-col-el items-end flex-1 min-w-0">
      <div className="flex-row-el h-[20px] relative shrink-0 items-baseline bg-white px-3 pt-[1px] rounded-t-xl">
        <p className="text-body text-brown-60 text-[18px] leading-4">{finished}</p>
        <p className="text-body text-brown-60 text-[14px]">/7</p>
      </div>

      <div className="flex-row-el items-center justify-end w-full h-[28px] bg-white rounded-b-xl py-[5px] pl-8 pr-2">
        <div
          className="flex-row-el items-center flex-1 h-full rounded-lg overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #8E979E 0%, #B3BEC8 100%)",
            boxShadow:
              "inset 3.03894e-16px 4.96296px 4.96296px rgba(137, 145, 152, 0.5), inset -3.03894e-16px -4.96296px 4.96296px rgba(184, 195, 206, 0.5)",
          }}
        >
          {finished > 0 && (
            <div className="flex-row-el flex-center">
              <IconCourseProgressStartBlock />
            </div>
          )}

          {finished > 1 &&
            Array(finished - 1)
              .fill(0)
              .map((_, blockIdx) => (
                <div key={blockIdx} className="flex-row-el flex-center -ml-1">
                  <IconCourseProgressBlock />
                </div>
              ))}

          {finished === 7 && (
            <>
              <div className="flex-row-el flex-center -ml-1">
                <IconCourseProgressEndBlock />
              </div>
              <div className="flex-col-el absolute -right-5 top-2">
                <IconMedal />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

export const UserProgressCourseCleared = ({ selected }: { selected: boolean }) => {
  const localizedImage = useLocalizedImage();
  const play = usePlaySound();
  const enableLab = useSelector((state: RootState) => !!state.config.parentConfig?.enable_lab);

  const availableCourses = useMemo(
    () => COURSE_DEFS.filter((def) => def.isEnabled(enableLab)),
    [enableLab],
  );

  const [selectedCourse, setSelectedCourse] = useState<Course>(availableCourses[0].course);

  const selectedDef =
    availableCourses.find((def) => def.course === selectedCourse) ?? availableCourses[0];

  const summaries = useSelector(
    (state: RootState) => availableCourses.map((def) => def.selectSummary(state)),
    shallowEqual,
  );
  const selectedProgresses = useSelector(selectedDef.selectProgresses, shallowEqual);
  const selectedSummary = useSelector(selectedDef.selectSummary);
  const selectedPercent = computePercent(selectedSummary);

  const handleSelect = (course: Course) => () => {
    if (course === selectedCourse) return;
    play();
    setSelectedCourse(course);
  };

  return (
    <div
      className="flex-col-el flex-1 w-full h-full overflow-hidden p-4 gap-4"
      style={{ display: selected ? undefined : "none" }}
    >
      <div className="flex-row-el shrink-0 items-center gap-3 bg-beige-20 rounded-2xl p-4 overflow-x-auto">
        {availableCourses.map((def, idx) => (
          <ShelfTile
            key={def.course}
            artUrl={localizedImage(def.art)}
            percent={computePercent(summaries[idx])}
            selected={def.course === selectedCourse}
            onClick={handleSelect(def.course)}
          />
        ))}
      </div>

      <div className="flex-row-el flex-1 min-h-0 bg-beige-20 rounded-2xl p-4 gap-4 items-stretch">
        <Hero
          title={I18n.t(selectedDef.labelKey)}
          percent={selectedPercent}
          finished={selectedSummary.finished}
          total={selectedSummary.total}
        />

        <div
          className="flex-col-el flex-1 min-w-0 self-stretch bg-white rounded-2xl p-4 overflow-hidden"
          style={{
            filter:
              "drop-shadow(-1px 1px 1px rgba(63, 74, 97, 0.15)) drop-shadow(1px 1px 3px rgba(63, 74, 97, 0.25))",
          }}
        >
          {selectedProgresses.length === 0 ? (
            <div className="flex-col-el flex-center w-full flex-1">
              <div className="dots-loader" />
            </div>
          ) : (
            <div className="flex-col-el w-full h-full overflow-y-auto pl-2 pr-1 py-2">
              <div className="grid grid-cols-2 gap-x-8 gap-y-6 w-full h-full content-between">
                {selectedProgresses.map((progress, idx) => (
                  <div key={progress.id} className="flex-row-el items-center w-full">
                    <StageCard
                      index={idx + 1}
                      finished={progress.stage.finished}
                      total={progress.stage.total || STAGE_TOTAL}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
